import React, { useState, useEffect, useRef } from 'react';
import './DateRangePicker.css';
import { DateRange } from 'react-day-picker';

interface Props {
	from?: Date;
	to?: Date;
	onChange: (range: DateRange) => void;
	onCancel: () => void;
	onConfirm: (range: DateRange) => void;
}

function getDaysArray(year: number, month: number) {
	const firstDay = new Date(year, month, 1);
	const lastDay = new Date(year, month + 1, 0);
	const prevLastDay = new Date(year, month, 0);

	const days: { day: number, type: 'prev' | 'current' | 'next' }[] = [];

	// 이번 달 1일의 요일(0:일~6:토)
	const firstDayOfWeek = firstDay.getDay(); // 0:일~6:토
	const startOffset = firstDayOfWeek; // 일요일이 첫 칸

	// 1. 이전 달
	for (let i = startOffset; i > 0; i--) {
		days.push({ day: prevLastDay.getDate() - i + 1, type: 'prev' });
	}

	// 2. 이번 달
	for (let d = 1; d <= lastDay.getDate(); d++) {
		days.push({ day: d, type: 'current' });
	}

	// 3. 다음 달
	let nextDay = 1;
	while (days.length < 42) {
		days.push({ day: nextDay++, type: 'next' });
	}
	return days;
}

function CalendarMonth({
	year,
	month,
	selected,
	onSelect,
	today,
	label,
	onYMChange,
	onDayDoubleClick,
}: {
	year: number;
	month: number;
	selected?: Date;
	onSelect: (date: Date) => void;
	today: Date;
	label: string;
	onYMChange: (type: 'year' | 'month', value: number) => void;
	onDayDoubleClick?: (date: Date) => void;
}) {
	const days = getDaysArray(year, month);
	const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
	// 연도 select 박스 옵션 생성
	const baseYears = Array.from({ length: 200 }, (_, i) => 1900 + i);
	const years = baseYears.includes(year) ? baseYears : [...baseYears, year].sort((a, b) => a - b);
	return (
		<div className="custom-calendar">
			<div className="calendar-label">{label}</div>
			<div className="calendar-header">
				<button type="button" onClick={() => onYMChange('year', year - 1)}>{'≪'}</button>
				<button type="button" onClick={() => onYMChange('month', month === 0 ? 11 : month - 1)}>{'<'}</button>
				<select value={year} onChange={e => onYMChange('year', Number(e.target.value))}>
					{years.map(y => 
						<option key={y} value={y}>{y}년</option>
					)}
				</select>
				<select value={month + 1} onChange={e => onYMChange('month', Number(e.target.value) - 1)}>
					{Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
				</select>
				<button type="button" onClick={() => onYMChange('month', month === 11 ? 0 : month + 1)}>{'>'}</button>
				<button type="button" onClick={() => onYMChange('year', year + 1)}>{'≫'}</button>
			</div>
			<div className="calendar-grid">
				{weekDays.map((w, i) => (
					<div
						key={w}
						className={`calendar-weekday${i === 0 ? ' sunday' : ''}${i === 6 ? ' saturday' : ''}`}
					>
						{w}
					</div>
				))}
				{days.map((d, i) => {
					// 날짜 객체 생성 (이전/다음달은 실제 날짜로 변환)
					let dateObj: Date;
					if (d.type === 'current') {
						dateObj = new Date(year, month, d.day);
					} else if (d.type === 'prev') {
						const prevMonth = month === 0 ? 11 : month - 1;
						const prevYear = month === 0 ? year - 1 : year;
						dateObj = new Date(prevYear, prevMonth, d.day);
					} else {
						const nextMonth = month === 11 ? 0 : month + 1;
						const nextYear = month === 11 ? year + 1 : year;
						dateObj = new Date(nextYear, nextMonth, d.day);
					}
					const isToday = dateObj.toDateString() === today.toDateString();
					const isSelected = selected && dateObj.toDateString() === selected.toDateString();
					const dayOfWeek = dateObj.getDay();
					const isSunday = dayOfWeek === 0;
					const isSaturday = dayOfWeek === 6;
					const className = `calendar-day${isSelected ? ' selected' : ''}${isToday ? ' today' : ''}${isSunday ? ' sunday' : ''}${isSaturday ? ' saturday' : ''}${d.type !== 'current' ? ' out' : ''}`;
					return (
						<button
							key={i}
							className={className}
							onClick={() => onSelect(dateObj)}
							onDoubleClick={onDayDoubleClick ? () => onDayDoubleClick(dateObj) : undefined}
							disabled={d.type !== 'current'}
							style={d.type !== 'current' ? { color: '#bbb', background: '#f4f4f4', cursor: 'default' } : {}}
						>
							{d.day}
						</button>
					);
				})}
			</div>
		</div>
	);
}

export default function DateRangePicker({ from, to, onChange, onCancel, onConfirm }: Props) {
	const today = new Date();
	const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

	const [start, setStart] = useState<Date | undefined>(from ?? firstDayOfMonth);
	const [end, setEnd] = useState<Date | undefined>(to ?? today);
	const [startYM, setStartYM] = useState({ year: (from ?? firstDayOfMonth).getFullYear(), month: (from ?? firstDayOfMonth).getMonth() });
	const [endYM, setEndYM] = useState({ year: (to ?? today).getFullYear(), month: (to ?? today).getMonth() });

	useEffect(() => {
		setStart(from ?? firstDayOfMonth);
		setEnd(to ?? today);
		setStartYM({ year: (from ?? firstDayOfMonth).getFullYear(), month: (from ?? firstDayOfMonth).getMonth() });
		setEndYM({ year: (to ?? today).getFullYear(), month: (to ?? today).getMonth() });
	}, [from, to]);

	// 기간 버튼 핸들러
	const setRange = (fromDate: Date, toDate: Date) => {
		const newFromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
		const newToDate = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
		setStart(newFromDate);
		setEnd(newToDate);
		setStartYM({ year: newFromDate.getFullYear(), month: newFromDate.getMonth() });
		setEndYM({ year: newToDate.getFullYear(), month: newToDate.getMonth() });
		onChange({ from: newFromDate, to: newToDate });
	};

	// 오늘, 전일, 주간, 분기, 반기, 월 등 버튼 동작
	const handlePeriodClick = (type: string, value?: number) => {
		const now = new Date();
		let fromDate = new Date(now);
		let toDate = new Date(now);
		switch (type) {
			case 'today':
				break;
			case 'yesterday':
				fromDate.setDate(now.getDate() - 1);
				toDate.setDate(now.getDate() - 1);
				break;
			case 'week':
				// 현재 날짜를 기준으로 이전 주간 설정
				fromDate = new Date(now);
				fromDate.setDate(now.getDate() - 7);  // 7일 전
				toDate = new Date(now);
				toDate.setDate(now.getDate() - 1);    // 어제
				break;
			case 'month':
				fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
				toDate.setDate(now.getDate());
				break;
			case 'quarter':
				if (typeof value === 'number') {
					const startMonth = (value - 1) * 3;
					fromDate = new Date(now.getFullYear(), startMonth, 1);
					toDate = new Date(now.getFullYear(), startMonth + 3, 0);
				}
				break;
			case 'half':
				if (value === 1) {
					fromDate = new Date(now.getFullYear(), 0, 1);
					toDate = new Date(now.getFullYear(), 6, 0);
				} else {
					fromDate = new Date(now.getFullYear(), 6, 1);
					toDate = new Date(now.getFullYear(), 12, 0);
				}
				break;
			default:
				break;
		}
		setRange(fromDate, toDate);
	};

	const handleStartSelect = (date: Date) => {
		const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
		setStart(newDate);
		onChange({ from: newDate, to: end });
	};
	const handleEndSelect = (date: Date) => {
		const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
		setEnd(newDate);
		onChange({ from: start, to: newDate });
	};
	const handleReset = () => {
		const newDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
		setStart(newDate);
		setEnd(newDate);
		setStartYM({ year: newDate.getFullYear(), month: newDate.getMonth() });
		setEndYM({ year: newDate.getFullYear(), month: newDate.getMonth() });
		onChange({ from: newDate, to: newDate });
	};
	const handleNowYearSet = () => {

		
		const newStartYM = { ...startYM, year: today.getFullYear(), month: 0, day: 1 };
		const newEndYM = { ...endYM, year: today.getFullYear(), month: today.getMonth(), day: today.getDate() };
		setStartYM(newStartYM);
		setEndYM(newEndYM);
		setStart(new Date(newStartYM.year, newStartYM.month, newStartYM.day));
		setEnd(new Date(newEndYM.year, newEndYM.month, newEndYM.day));
		onChange({ 
			from: new Date(newStartYM.year, newStartYM.month, newStartYM.day), 
			to: new Date(newEndYM.year, newEndYM.month, newEndYM.day) 
		});
	}
	const handleNowTodaySet = () => {
		setStartYM({ ...startYM, month: today.getMonth() });
		setEndYM({ ...endYM, month: today.getMonth() });
		setStart(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
		setEnd(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
		onChange({ 
			from: new Date(today.getFullYear(), today.getMonth(), today.getDate()), 
			to: new Date(today.getFullYear(), today.getMonth(), today.getDate()) 
		});
	}

	const rootRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		rootRef.current?.focus();
	}, []);

	const handleRootKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key !== 'Enter') return;
		const t = e.target as HTMLElement;
		if (t.closest('button') || t.closest('select')) return;
		e.preventDefault();
		e.stopPropagation();
		if (start == null || end == null) return;
		onConfirm({ from: start, to: end });
	};

	return (
		<div
			ref={rootRef}
			className="custom-range-picker-popup"
			tabIndex={0}
			onKeyDown={handleRootKeyDown}
		>
			{/* 상단 기간 버튼 2줄 */}
			<div className="calendar-period-btns">
				<div className="calendar-period-row">
					{/* <button onClick={() => handlePeriodClick('today')}>오늘</button> */}
					<button onClick={() => handlePeriodClick('yesterday')}>전일</button>
					<button onClick={() => handlePeriodClick('week')}>주간</button>
					<button onClick={() => handlePeriodClick('month')}>당월</button>
					<button onClick={() => handlePeriodClick('quarter', 1)}>1분기</button>
					<button onClick={() => handlePeriodClick('quarter', 2)}>2분기</button>
					<button onClick={() => handlePeriodClick('quarter', 3)}>3분기</button>
					<button onClick={() => handlePeriodClick('quarter', 4)}>4분기</button>
					<button onClick={() => handlePeriodClick('half', 1)}>상반기</button>
					<button onClick={() => handlePeriodClick('half', 2)}>하반기</button>
				</div>
			</div>
			{/* 달력 2개 */}
			<div className="calendar-container">
				<div>
					<CalendarMonth
						year={startYM.year}
						month={startYM.month}
						selected={start}
						onSelect={handleStartSelect}
						today={today}
						label=""
						onYMChange={(type, value) => {
							if (type === 'year') setStartYM({ ...startYM, year: value });
							else setStartYM({
								year: value === 11 && startYM.month === 0 ? startYM.year - 1 : value === 0 && startYM.month === 11 ? startYM.year + 1 : startYM.year,
								month: value
							});
						}}
						onDayDoubleClick={() => onConfirm({ from: start, to: end })}
					/>
				</div>
				<div>
					<CalendarMonth
						year={endYM.year}
						month={endYM.month}
						selected={end}
						onSelect={handleEndSelect}
						today={today}
						label=""
						onYMChange={(type, value) => {
							if (type === 'year') setEndYM({ ...endYM, year: value });
							else setEndYM({
								year: value === 11 && endYM.month === 0 ? endYM.year - 1 : value === 0 && endYM.month === 11 ? endYM.year + 1 : endYM.year,
								month: value
							});
						}}
						onDayDoubleClick={() => onConfirm({ from: start, to: end })}
					/>
				</div>
			</div>
			{/* 하단 안내문구 */}
			<div className="calendar-footer">
				<div className="calendar-btns">
					<div className="left-btns">
						<button 
							className="cancel" 
							onClick={(e) => { 
								e.stopPropagation();  // 이벤트 버블링 방지
								onCancel(); 
							}}
						>
							취소
						</button>
					</div>
					<div className="right-btns">
						<button className="reset" onClick={handleNowYearSet}>올해</button>
						<button className="reset" onClick={handleNowTodaySet}>오늘</button>
						<button className="confirm" onClick={(e) => {
							e.stopPropagation();
							onConfirm({ from: start, to: end });
						}}>확인</button>
					</div>
				</div>
			</div>
			
		</div>
	);
} 